'use client';

import { useAgeVerification, useAgeVerificationPending } from '@/hooks/useAgeVerification';
import { AgeVerificationModal } from './AgeVerificationModal';

/**
 * Drives the age-question modal (MWBI-899).
 *
 * Rule: a signed-in member who has never answered "Are you 18 or older?" is asked
 * before they can use the app. The answer is recorded server-side (ageVerifiedAt);
 * once set, the question never appears again — for that member, on any device.
 *
 * It appears on the member's next page load, not only after a fresh sign-in,
 * because it is driven by /api/auth/me state rather than by the login flow. That
 * is what lets existing members be asked at launch without signing anyone out.
 *
 * The modal is blocking by design: no onClose is passed, so there is no X button,
 * no backdrop dismiss, and no Escape. The only way out is answering.
 *
 * Ordering: this renders before CommunityGuidelinesGate, which suppresses itself
 * while an age answer is outstanding — so a brand-new member answers the age
 * question first and then meets the guidelines, and a returning member who has
 * already accepted the guidelines only ever sees the age question.
 */
export function AgeVerificationGate() {
  const isPending = useAgeVerificationPending();
  const { submit, saving, error } = useAgeVerification();

  return (
    <AgeVerificationModal
      isOpen={isPending}
      onSubmit={submit}
      saving={saving}
      errorCode={error}
    />
  );
}
